/* Buxmed AOV/Momsify display adapter v1.3.0 — TEST IN A THEME COPY ONLY.
 * Contract: AOV supplies EUR net prices with 19% VAT, in German notation.
 * No cart, discount, variant, app data, or Storage prototype is modified.
 */
(() => {
  'use strict';
  const KEY = '__buxmedAovMomsify';
  if (window[KEY]) { window[KEY].refresh(); return; }
  const ROOT = '.Avada-Bundle-Volume__Container';
  const TARGET = '.AOV-Offer__DiscountPrice, .Avada-Volume__Info--Saving, .aov-sr-only';
  const STORAGE_KEY = 'momsifyKundtyp';
  const NOTE = '[data-buxmed-aov-tax-note]';
  const WATERMARK = '.momsify-watermark';
  const format = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
  // Currency is mandatory: quantities and percentages must never be converted.
  const money = /(?<![\d.,\-−])(?:\d{1,3}(?:\.\d{3})+|\d+)(?:,\d{2})?[\s\u00a0\u202f]*(?:€|EUR\b)/g;
  let states = new WeakMap();
  let timer = null;
  let stopped = false;
  let writes = 0;
  let mode = readMode();
  function readMode() {
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      // Empirically confirmed inverted mapping, intentionally not inferred.
      return value === 'private' ? 'net' : value === 'business' ? 'gross' : 'unknown';
    } catch (_) { return 'unknown'; }
  }
  function render(source, currentMode) {
    if (currentMode !== 'gross') return source;
    return source.replace(money, (token) => {
      const net = Number(token.replace(/(?:€|EUR)|\s/g, '').replace(/\./g, '').replace(',', '.'));
      if (!Number.isFinite(net)) return token;
      return format.format(Math.round((net * 1.19 + Number.EPSILON) * 100) / 100);
    });
  }
  function nodes() {
    const result = new Set();
    document.querySelectorAll(ROOT).forEach(root => {
      root.querySelectorAll(TARGET).forEach(target => {
        const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
        let node;
        while ((node = walker.nextNode())) {
          if (!node.parentElement.closest('script, style, textarea, [contenteditable="true"]')) result.add(node);
        }
      });
    });
    return result;
  }
  function observe() {
    observer.observe(document.documentElement, {
      subtree: true, childList: true, characterData: true,
      attributes: true, attributeFilter: ['class']
    });
  }
  function updateTaxNotes() {
    document.querySelectorAll(ROOT).forEach(root => {
      const existing = [...root.querySelectorAll(NOTE)].filter(note => note.closest(ROOT) === root);
      if (mode === 'unknown') {
        existing.forEach(note => note.remove());
        return;
      }
      let note = existing.shift();
      existing.forEach(duplicate => duplicate.remove());
      if (!note) {
        note = document.createElement('p');
        note.setAttribute('data-buxmed-aov-tax-note', '');
        note.className = 'buxmed-aov-tax-note';
        note.setAttribute('aria-live', 'polite');
        note.setAttribute('aria-atomic', 'true');
        note.style.cssText = 'display:block;width:100%;flex-basis:100%;grid-column:1 / -1;margin:8px 0 0;font-size:0.85em;line-height:1.4;font-weight:400;';
        root.appendChild(note);
      }
      const text = mode === 'gross' ? 'Preise inkl. MwSt.' : 'Preise exkl. MwSt.';
      if (note.textContent !== text) note.textContent = text;
    });
  }
  function invalidate(records) {
    // External character writes are fresh net input, even when numerically
    // identical to our last gross output. Our own writes are never observed.
    records.forEach(record => {
      if (record.type === 'characterData') states.delete(record.target);
    });
  }
  function sync() {
    timer = null;
    if (stopped) return;
    invalidate(observer.takeRecords());
    mode = readMode();
    observer.disconnect();
    try {
      document.querySelectorAll(WATERMARK).forEach(watermark => watermark.remove());
      nodes().forEach(node => {
        let state = states.get(node);
        if (!state || node.data !== state.output) {
          state = { source: node.data, output: node.data };
          states.set(node, state);
        }
        const output = render(state.source, mode);
        if (node.data !== output) { node.data = output; writes++; }
        state.output = output;
      });
      updateTaxNotes();
    } finally { if (!stopped) observe(); }
  }
  function schedule() {
    if (!stopped && timer === null) timer = setTimeout(sync, 0);
  }
  const observer = new MutationObserver(records => {
    invalidate(records);
    if (records.some(record => {
      const element = record.target.nodeType === 1 ? record.target : record.target.parentElement;
      return element?.closest(ROOT) || element?.matches(WATERMARK) || (record.type === 'childList' &&
        [...record.addedNodes].some(node => node.nodeType === 1 &&
          (node.matches(`${ROOT}, ${WATERMARK}`) || node.querySelector(`${ROOT}, ${WATERMARK}`))));
    })) schedule();
  });
  function checkMode() { if (readMode() !== mode) schedule(); }
  function storage(event) { if (event.key === STORAGE_KEY || event.key === null) checkMode(); }
  // The storage event does not fire in the writing tab. Poll only the single
  // key; scan the DOM only when its value changes. No global monkey patching.
  const interval = setInterval(() => { if (!document.hidden) checkMode(); }, 250);
  window.addEventListener('storage', storage);
  window.addEventListener('pageshow', schedule);
  document.addEventListener('visibilitychange', checkMode);
  document.addEventListener('change', schedule, true);
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('buxmed:momsify-change', schedule);
  window[KEY] = {
    version: '1.3.0', refresh: schedule,
    status: () => ({ mode: readMode(), writes, stopped }),
    destroy({ restore = true } = {}) {
      stopped = true;
      clearTimeout(timer);
      clearInterval(interval);
      invalidate(observer.takeRecords());
      observer.disconnect();
      document.querySelectorAll(NOTE).forEach(note => note.remove());
      window.removeEventListener('storage', storage);
      window.removeEventListener('pageshow', schedule);
      document.removeEventListener('visibilitychange', checkMode);
      document.removeEventListener('change', schedule, true);
      document.removeEventListener('shopify:section:load', schedule);
      document.removeEventListener('buxmed:momsify-change', schedule);
      if (restore) nodes().forEach(node => {
        const state = states.get(node);
        if (state && node.data === state.output) node.data = state.source;
      });
      states = new WeakMap();
      delete window[KEY];
    }
  };
  observe();
  schedule();
})();
